import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import { Collapse, Badge, Box, Group, Text, Stack } from '@mantine/core';
import { RuleViolation } from '../types/rule';
interface RuleViolationsAlertProps {
  violations: RuleViolation[];
  onSelectRule: (violation: RuleViolation | null) => void;
  activeRuleId: string | null;
}
export function RuleViolationsAlert({
  violations,
  onSelectRule,
  activeRuleId
}: RuleViolationsAlertProps) {
  const [isOpen, setIsOpen] = useState(false);
  if (violations.length === 0) {
    return null;
  }
  const totalViolations = violations.reduce((sum, v) => sum + v.affectedLoanIds.length, 0);
  const criticalCount = violations.filter(v => v.severity === 'critical').length;
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'rgba(239, 68, 68, 0.8)';
      case 'warning':
        return 'rgba(245, 158, 11, 0.8)';
      case 'info':
        return 'rgba(59, 130, 246, 0.8)';
      default:
        return 'rgba(255, 255, 255, 0.4)';
    }
  };
  const getRowBg = (severity: string, isActive: boolean) => {
    if (isActive) {
      return 'rgba(59, 130, 246, 0.15)';
    }
    switch (severity) {
      case 'critical':
        return 'rgba(239, 68, 68, 0.08)';
      case 'warning':
        return 'rgba(245, 158, 11, 0.08)';
      default:
        return 'rgba(59, 130, 246, 0.08)';
    }
  };
  const getRowBorder = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'rgba(239, 68, 68, 0.2)';
      case 'warning':
        return 'rgba(245, 158, 11, 0.2)';
      default:
        return 'rgba(59, 130, 246, 0.2)';
    }
  };
  const activeViolation = violations.find(v => v.ruleId === activeRuleId);
  return <Stack gap="md" data-id="element-414">
      {/* Main Alert Banner */}
      <Box style={{
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderRadius: '12px',
      border: '1px solid rgba(239, 68, 68, 0.2)',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
    }} data-id="element-415">
        <Box onClick={() => setIsOpen(!isOpen)} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        backgroundColor: 'rgba(239, 68, 68, 0.08)',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
      }} data-id="element-416">
          <Group gap="md" data-id="element-417">
            <Box style={{
            padding: '8px',
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            borderRadius: '8px'
          }} data-id="element-418">
              <AlertTriangle size={20} color="#f87171" data-id="element-419" />
            </Box>
            <Box data-id="element-420">
              <Text fw={700} size="lg" style={{
              color: 'rgba(255, 255, 255, 0.95)'
            }} data-id="element-421">
                {violations.length} Rules Violated
              </Text>
              <Text size="sm" style={{
              color: 'rgba(255, 255, 255, 0.5)'
            }} data-id="element-422">
                Affecting {totalViolations} total loans across pipeline
              </Text>
            </Box>
          </Group>
          <Group gap="md" data-id="element-423">
            {criticalCount > 0 && <Badge color="red" variant="light" size="lg" style={{
            fontWeight: 700
          }} data-id="element-424">
                {criticalCount} Critical
              </Badge>}
            {isOpen ? <ChevronUp size={20} color="rgba(255, 255, 255, 0.4)" data-id="element-425" /> : <ChevronDown size={20} color="rgba(255, 255, 255, 0.4)" data-id="element-426" />}
          </Group>
        </Box>

        <Collapse in={isOpen} data-id="element-427">
          <Stack gap="sm" p="md" style={{
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(239, 68, 68, 0.1)'
        }} data-id="element-428">
            {violations.map(violation => <Box key={violation.ruleId} onClick={() => onSelectRule(activeRuleId === violation.ruleId ? null : violation)} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${getRowBorder(violation.severity)}`,
            backgroundColor: getRowBg(violation.severity, activeRuleId === violation.ruleId),
            cursor: 'pointer',
            transition: 'all 0.2s',
            ...(activeRuleId === violation.ruleId && {
              boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.5)'
            })
          }} data-id="element-429">
                <Group gap="md" data-id="element-430">
                  <Box style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: getSeverityColor(violation.severity),
                boxShadow: `0 0 8px ${getSeverityColor(violation.severity)}`
              }} data-id="element-431" />
                  <Box data-id="element-432">
                    <Text fw={600} style={{
                  color: 'rgba(255, 255, 255, 0.9)'
                }} data-id="element-433">
                      {violation.ruleName}
                    </Text>
                    <Text size="xs" tt="uppercase" style={{
                  color: 'rgba(255, 255, 255, 0.4)'
                }} data-id="element-434">
                      {violation.severity}
                    </Text>
                  </Box>
                </Group>

                <Group gap="md" data-id="element-435">
                  <Badge variant="light" size="lg" color="gray" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600
              }} data-id="element-436">
                    {violation.affectedLoanIds.length} Loans
                  </Badge>
                  <Box component="button" style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#60a5fa',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }} data-id="element-437">
                    View Loans
                  </Box>
                </Group>
              </Box>)}
          </Stack>
        </Collapse>
      </Box>

      {/* Active Filter Chip */}
      {activeViolation && <Box style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '8px',
      padding: '12px'
    }} data-id="element-438">
          <Group gap="md" data-id="element-439">
            <Box style={{
          padding: '6px',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          borderRadius: '6px'
        }} data-id="element-440">
              <Filter size={16} color="#60a5fa" data-id="element-441" />
            </Box>
            <Text size="sm" style={{
          color: 'rgba(255, 255, 255, 0.8)'
        }} data-id="element-442">
              Filtered by rule:{' '}
              <Text span fw={700} style={{
            color: '#60a5fa'
          }} data-id="element-443">
                {activeViolation.ruleName}
              </Text>
            </Text>
            <Badge size="sm" variant="filled" color="blue" data-id="element-444">
              {activeViolation.affectedLoanIds.length} results
            </Badge>
          </Group>
          <Box component="button" onClick={() => onSelectRule(null)} style={{
        padding: '6px',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s'
      }} data-id="element-445">
            <X size={16} color="#60a5fa" data-id="element-446" />
          </Box>
        </Box>}
    </Stack>;
}